import { cookies, setCookie, removeCookie } from '@zing/shared/cookies/behavior'
import {
  getCredLocalStorage,
  setCredLocalStorage,
  removeCredLocalStorage,
  ICredLocalStorage
} from './credLocalStorage'
import { LOGOUT, SET_AUTH, SET_PERMISSIONS } from './authActions'

// Keys stripped from the permissions payload before it is persisted to the
// `_perm` cookie. The raw payload carries a UUID `id` and an `action` on every
// feature/access; URL-encoded it grows to ~10KB and blows past the browser's
// ~4KB per-cookie limit, so the cookie is silently dropped. Dropping the
// ids/actions shrinks it while keeping the `code`/`accesses[].code` that
// hasAccess() consumes. Redux still holds the full payload in memory.
const PERM_COOKIE_DROP_KEYS = new Set(['id', 'action'])
const trimPermissionsForCookie = (payload: unknown): unknown =>
  JSON.parse(JSON.stringify(payload, (key, value) => (PERM_COOKIE_DROP_KEYS.has(key) ? undefined : value)))

// `_perm` can still exceed the ~4KB per-cookie limit for users with many
// features/accesses and be silently dropped by the browser — which leaves a
// freshly booted legacy (after a shell -> legacy redirect) with no permissions,
// collapsing the side menu to only "Configurações". The menu and route guards
// only need the feature *codes*, so persist them in a tiny, always-fits
// companion cookie that is independent of `_perm`'s size.
const extractFeatureCodesForCookie = (payload: any): string[] =>
  Array.isArray(payload?.features)
    ? payload.features.map((f: any) => String(f?.code)).filter((c: string) => c && c !== 'undefined')
    : []

const AUTH_DEFAULT_USER_VALUE = JSON.parse('{}')
const AUTH_DEFAULT_IS_AUTHENTICATED_VALUE = undefined
const AUTH_DEFAULT_PERMISSIONS_VALUE = undefined

const credCookieData = cookies.get('_cred')?.data
const credLocalStorageData = getCredLocalStorage()

export const AUTH_INITIAL_STATE: IAuthState = {
  user: credCookieData ? { ...credCookieData, ...credLocalStorageData } : AUTH_DEFAULT_USER_VALUE,
  isAuthenticated: cookies.get('_cred') ?? AUTH_DEFAULT_IS_AUTHENTICATED_VALUE,
  permissions: cookies.get('_perm') ?? AUTH_DEFAULT_PERMISSIONS_VALUE
}

interface IAuthUser {
  id: string
  username: string
  role: string
  token: string
}

export interface IAuthAccess {
  id: string
  code: string
  action?: string
}

export interface IAuthFeature {
  id: string
  code: string
  access: IAuthAccess[]
}

export interface IAuthPermission {
  id: string
  name: string
  features: IAuthFeature[]
}

export interface IAuthAction {
  type: string
  payload: Partial<IAuthState>
}

export interface IAuthState {
  user?: IAuthUser
  permissions?: IAuthPermission
  isAuthenticated: boolean
}

export const AuthReducer = (state: IAuthState = AUTH_INITIAL_STATE, action: IAuthAction): any => {
  switch (action.type) {
    case SET_AUTH:
      const cookieData = {
        id: (action.payload as any)?.id,
        data: {
          id: (action.payload as any)?.data?.id || (action.payload as any)?.id,
          email: (action.payload as any)?.data?.email,
          name: (action.payload as any)?.data?.name,
          token: (action.payload as any)?.data?.token,
          role: (action.payload as any)?.data?.role
        }
      }

      const localStorageData: ICredLocalStorage = {
        id: cookieData.data.id,
        temporaryToken: (action.payload as any)?.data?.temporaryToken,
        // Trust the backend's flag (`IsSuperAdmin` in the v2 login response) like the legacy
        // reducer does. Deriving it from `role.name === "Super Admin"` was always dead: no
        // backend ever emits that string — `Role.DisplayName` only produces "Administrator",
        // "CaixaEmployee" or "User" — so this evaluated to false for every real super admin
        // and the sidebar company search never rendered. The string check is kept as a
        // fallback in case a tenant DB really does name a role that way.
        isSuperAdmin: (action.payload as any)?.data?.isSuperAdmin
          ?? ((action.payload as any)?.data?.role?.name === "Super Admin"),
        company: (action.payload as any)?.data?.company ? {
          id: (action.payload as any)?.data?.company?.id,
          name: (action.payload as any)?.data?.company?.name,
          type: (action.payload as any)?.data?.company?.type,
          cityHallInfo: (action.payload as any)?.data?.company?.cityHallInfo
        } : undefined,
        profiles: (action.payload as any)?.data?.profiles ?
          (action.payload as any)?.data?.profiles.map((p: any) => ({
            id: p.id,
            role: { id: p.role?.id, name: p.role?.name },
            company: {
              id: p.company?.id,
              name: p.company?.name,
              contactName: p.company?.contactName,
              type: p.company?.type
            }
          })) : undefined,
        updatedAt: (action.payload as any)?.data?.updatedAt,
        powerBiUrl: (action.payload as any)?.data?.powerBiUrl,
        startupInfo: (action.payload as any)?.data?.startupInfo ? {
          isSimplifiedResolver: (action.payload as any)?.data?.startupInfo?.isSimplifiedResolver
        } : undefined,
        cityHallInfo: (action.payload as any)?.data?.cityHallInfo ? {
            Id: (action.payload as any)?.data?.cityHallInfo?.id,
            Name: (action.payload as any)?.data?.cityHallInfo?.name,
            MayorName: (action.payload as any)?.data?.cityHallInfo?.mayorName,
            TeamSize: (action.payload as any)?.data?.cityHallInfo?.teamSize,
            HasSecretary: (action.payload as any)?.data?.cityHallInfo?.hasSecretary,
            Secretary: (action.payload as any)?.data?.cityHallInfo?.secretary,
            SecretaryResponsible: (action.payload as any)?.data?.cityHallInfo?.secretaryResponsible
          }
        : undefined
      }

      setCookie('_cred', cookieData)
      setCredLocalStorage(localStorageData)

      return {
        ...state,
        user: action.payload,
        isAuthenticated: true
      }

    case SET_PERMISSIONS:
      setCookie('_perm', trimPermissionsForCookie(action.payload))
      setCookie('_permCodes', extractFeatureCodesForCookie(action.payload))
      return {
        ...state,
        permissions: action.payload
      }

    case LOGOUT:
      removeCookie('_cred')
      removeCookie('_perm')
      removeCookie('_permCodes')
      removeCookie('_usrData')
      removeCookie('_profImg')
      removeCookie('_behaveAsCompany')
      removeCredLocalStorage()
      return {
        user: AUTH_DEFAULT_USER_VALUE,
        isAuthenticated: AUTH_DEFAULT_IS_AUTHENTICATED_VALUE,
        permissions: AUTH_DEFAULT_PERMISSIONS_VALUE
      }
    default:
      return state
  }
}
